import { useRef, useEffect } from 'react';
import type React from 'react';

interface ParticleDef {
  top: string;
  left: string;
  dur: string;
  delay: string;
  size: 'sm' | 'md' | 'lg';
}

function generateParticles(count: number): ParticleDef[] {
  const arr: ParticleDef[] = [];
  const sizes: ParticleDef['size'][] = ['sm', 'sm', 'sm', 'sm', 'md', 'md', 'md', 'lg', 'lg'];
  for (let i = 0; i < count; i++) {
    arr.push({
      top: `${Math.random() * 100}%`,
      left: `${Math.random() * 100}%`,
      dur: `${4 + Math.random() * 8}s`,
      delay: `${Math.random() * 5}s`,
      size: sizes[Math.floor(Math.random() * sizes.length)],
    });
  }
  return arr;
}

const particleCount = 50;
const particles = generateParticles(particleCount);

export default function GoldParticles() {
  const containerRef = useRef<HTMLDivElement>(null);
  const particleRefs = useRef<(HTMLDivElement | null)[]>([]);
  const mouseRef = useRef({ x: -9999, y: -9999, active: false });
  const rafRef = useRef<number>(0);
  const lerpRef = useRef<{ x: number; y: number }[]>(
    particles.map(() => ({ x: 0, y: 0 })),
  );

  useEffect(() => {
    const handleMouseMove = (e: MouseEvent) => {
      mouseRef.current.x = e.clientX;
      mouseRef.current.y = e.clientY;
      mouseRef.current.active = true;
    };

    const handleMouseLeave = () => {
      mouseRef.current.active = false;
    };

    window.addEventListener('mousemove', handleMouseMove, { passive: true });
    document.addEventListener('mouseleave', handleMouseLeave);

    const animate = () => {
      const mouse = mouseRef.current;
      const w = window.innerWidth || 1;
      const h = window.innerHeight || 1;

      particleRefs.current.forEach((el, i) => {
        if (!el) return;
        const topStr = el.getAttribute('data-top') || '50%';
        const leftStr = el.getAttribute('data-left') || '50%';
        const pTop = (parseFloat(topStr) / 100) * h;
        const pLeft = (parseFloat(leftStr) / 100) * w;

        let targetX = 0;
        let targetY = 0;

        if (mouse.active) {
          const dx = mouse.x - pLeft;
          const dy = mouse.y - pTop;
          const dist = Math.sqrt(dx * dx + dy * dy);
          const maxDist = 500;
          const strength = Math.max(0, 1 - dist / maxDist) * 25;
          if (strength > 0) {
            targetX = (dx / (dist || 1)) * strength;
            targetY = (dy / (dist || 1)) * strength;
          }
        }

        const lerp = lerpRef.current[i];
        lerp.x += (targetX - lerp.x) * 0.06;
        lerp.y += (targetY - lerp.y) * 0.06;

        const baseTransform = (el.style.transform || '').replace(/translate\([^)]*\)/g, '').trim();
        el.style.transform = `${baseTransform} translate(${lerp.x.toFixed(1)}px, ${lerp.y.toFixed(1)}px)`;
      });

      rafRef.current = requestAnimationFrame(animate);
    };

    rafRef.current = requestAnimationFrame(animate);
    return () => {
      window.removeEventListener('mousemove', handleMouseMove);
      document.removeEventListener('mouseleave', handleMouseLeave);
      cancelAnimationFrame(rafRef.current);
    };
  }, []);

  return (
    <div
      ref={containerRef}
      className="fixed inset-0 z-0 overflow-hidden pointer-events-none"
    >
      {particles.map((p, i) => (
        <div
          key={i}
          ref={(el) => {
            particleRefs.current[i] = el;
          }}
          data-top={p.top}
          data-left={p.left}
          className={`gold-particle gold-particle-${p.size}`}
          style={
            {
              top: p.top,
              left: p.left,
              '--dur': p.dur,
              '--delay': p.delay,
            } as React.CSSProperties
          }
        />
      ))}
    </div>
  );
}